import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, CheckCircle2, RefreshCw } from 'lucide-react';
import {
    JobRef,
    JobStatusBadges,
    NeedsYouPayload,
    blockedOnLabel,
    formatRelativeTime,
    jobSubject,
} from '../services/servicesShared';
import { ApprovalDecideCard, GuidanceAnswerForm } from './JobQuestions';

const JobLink: React.FC<{ job: JobRef | null }> = ({ job }) => {
    if (!job) return <span className="text-xs text-muted-foreground">Job no longer available</span>;
    return (
        <Link to={`/jobs/${job.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <span className="font-medium text-foreground">{job.service_name || 'Service'}</span>
            {job.subject ? <span>· {job.subject}</span> : null}
            <ArrowUpRight className="h-3 w-3" />
        </Link>
    );
};

const Section: React.FC<{ title: string; description: string; count: number; children: React.ReactNode }> = ({
    title,
    description,
    count,
    children,
}) => (
    <section className="mb-8">
        <div className="mb-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
                {title}
                <span className="text-xs font-semibold rounded-xl px-2 py-0.5 bg-muted text-muted-foreground tabular-nums">{count}</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        {children}
    </section>
);

/**
 * The Needs-you queue (GET /jobs/needs-you): steward guidance to answer,
 * ask_vendor approvals to decide, and jobs blocked on the vendor's side.
 */
export const NeedsYou: React.FC<{ active?: boolean }> = ({ active = true }) => {
    const { get } = useApi();
    const [data, setData] = useState<NeedsYouPayload | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchQueue = useCallback(async () => {
        try {
            setLoading(true);
            const response = await get('/jobs/needs-you');
            setData(response.data);
        } catch (error) {
            console.error('Error fetching the Needs-you queue:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        if (active) void fetchQueue();
    }, [active, fetchQueue]);

    const guidance = data?.guidance || [];
    const approvals = data?.approvals || [];
    const blocked = data?.blocked_jobs || [];
    const empty = !loading && !guidance.length && !approvals.length && !blocked.length;

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-4">
                <p className="text-sm text-slate-500 max-w-2xl">
                    Everything your jobs are waiting on you for. Answers at service scope become playbook guidance every job of that
                    service reads at its next wake.
                </p>
                <Button variant="ghost" size="icon" title="Refresh" onClick={() => void fetchQueue()} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {loading && !data && (
                <div className="animate-pulse space-y-3">
                    <div className="h-16 bg-muted rounded" />
                    <div className="h-16 bg-muted rounded" />
                </div>
            )}

            {empty && (
                <div className="flex items-center gap-2 rounded-lg border px-4 py-6 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-green-600" /> Nothing needs you right now.
                </div>
            )}

            {guidance.length > 0 && (
                <Section
                    title="Questions"
                    description="The job made a judgment call it wants your policy on."
                    count={guidance.length}
                >
                    <div className="space-y-3">
                        {guidance.map((request) => (
                            <div key={request.id} className="rounded-lg border p-3 space-y-2">
                                <JobLink job={request.job} />
                                <GuidanceAnswerForm
                                    compact
                                    request={request}
                                    onAnswered={() =>
                                        setData((prev) =>
                                            prev ? { ...prev, guidance: prev.guidance.filter((item) => item.id !== request.id) } : prev,
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {approvals.length > 0 && (
                <Section
                    title="Approvals"
                    description="Writes the service's policy says you approve before the job runs them."
                    count={approvals.length}
                >
                    <div className="space-y-3">
                        {approvals.map((approval) => (
                            <div key={approval.id} className="rounded-lg border p-3 space-y-2">
                                <JobLink job={approval.job} />
                                <ApprovalDecideCard
                                    compact
                                    approval={approval}
                                    onDecided={() =>
                                        setData((prev) =>
                                            prev ? { ...prev, approvals: prev.approvals.filter((item) => item.id !== approval.id) } : prev,
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {blocked.length > 0 && (
                <Section
                    title="Blocked jobs"
                    description="On, but not working until someone acts. Open the job to unblock it."
                    count={blocked.length}
                >
                    <div className="rounded-lg border divide-y">
                        {blocked.map((job) => (
                            <Link key={job.id} to={`/jobs/${job.id}`} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-muted/50">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">
                                        {job.service_name || 'Service'} · {jobSubject(job)}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {blockedOnLabel(job.blocked_on)}
                                        {job.blocked_detail?.question ? ` — ${job.blocked_detail.question}` : ''}
                                        {job.blocked_detail?.tool ? ` — ${job.blocked_detail.tool}` : ''}
                                        {' · '}since {formatRelativeTime(job.updated_at)}
                                    </div>
                                </div>
                                <JobStatusBadges job={job} />
                            </Link>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
};

export default NeedsYou;
